import {
  type CompanyTag,
  type Currency,
  type DataQuality,
  type FundingRound,
  type Headcount,
  type Job,
} from "@prisma/client";
import { type AppContext } from "~/lib/context";
import { chain, maxBy, minBy, sumBy } from "~/lib/lodash";
import { weightedMeanBy } from "~/lib/math";
import { isNotNull } from "~/lib/utils";
import { combineDataQuality } from "~/services/dataQuality";
import { type Measure } from "~/services/employee";
import { type EmployeeLevel } from "~/services/employee/employeeLevel";
import { type WeightedJob } from "~/services/job";
import { computeJobsCompensationData } from "~/services/market-data/dataset-employee/compute-compensations/computeJobsCompensationData";
import { type MarketDataDatasetEmployee } from "~/services/market-data/dataset-employee/getMarketDataDatasetEmployees";
import { type EmployeeLocationsForPredictiveBenchmark } from "~/services/market-data/getMarketDataPredictiveBenchmark";
import { BOTTOM_PERCENTILE, TOP_PERCENTILE, type MarketDataCompensationData } from "~/services/marketCompensationStats";
import { type PercentileBounds } from "~/services/percentiles";

type ComputeEmployeesCompensationDataOptions<T extends Measure> = {
  currency: Currency;
  measures: T[];
  level?: EmployeeLevel;
  locations: EmployeeLocationsForPredictiveBenchmark;
  percentiles: PercentileBounds;
  targetPercentile?: number;
  fallbackToPredictiveData?: boolean;
  fundingRounds?: FundingRound[];
  headcount?: Headcount | null;
  industry?: CompanyTag | null;
};

type ComputeMarketCompensationDataOptions<T extends Measure> = ComputeEmployeesCompensationDataOptions<T> & {
  weightedJobs: WeightedJob[];
};

export const computeMarketCompensationData = async <T extends Measure>(
  ctx: AppContext,
  datasetEmployees: MarketDataDatasetEmployee[],
  options: ComputeMarketCompensationDataOptions<T>
): Promise<ComputeMarketCompensationDataResult<T>> => {
  // For each weighted job, compute counts & percentile stats
  const jobsCompensationData = await computeJobsCompensationData(ctx, datasetEmployees, options);

  // Record jobs for which we couldn't get relevant compensation data
  const missingJobs = jobsCompensationData.filter((item) => !item.measures).map((item) => item.job);

  const relevantJobsCompensationData = jobsCompensationData.filter((item) => {
    return !!item.measures;
  }) as ((typeof jobsCompensationData)[0] & { measures: NonNullable<(typeof jobsCompensationData)[0]["measures"]> })[];

  // If we couldn't find any relevant data for any requested job, skip stats

  if (!relevantJobsCompensationData.length) {
    return { missingJobs, data: null };
  }

  // Compute the number of employees & companies for which we were able to compute compensation data

  const employeesCount = sumBy(relevantJobsCompensationData, (row) => row.employeesCount);

  const distinctCompaniesCount = chain(relevantJobsCompensationData)
    .flatMap((row) => row.distinctCompaniesIds)
    .uniq()
    .size()
    .value();

  // For each requested measure, aggregate weighted jobs compensation data

  const measures = chain(options.measures)
    .keyBy()
    .mapValues((measure) => {
      const dataQualityDetailledByJob = relevantJobsCompensationData
        .map((row) => {
          const dataQuality = row.measures[measure]?.dataQuality ?? null;

          if (!dataQuality) {
            return null;
          }

          return {
            job: { id: row.job.id, name: row.job.name },
            employeesCount: row.employeesCount,
            distinctCompaniesCount: row.distinctCompaniesIds.length,
            dataQuality,
          };
        })
        .filter(isNotNull);

      return {
        dataQuality: combineDataQuality(dataQualityDetailledByJob.map((row) => row.dataQuality)),
        dataQualityDetailledByJob,

        median: Math.round(
          weightedMeanBy(relevantJobsCompensationData, (row) => {
            return [row.measures[measure]?.median ?? null, row.weight];
          })
        ),

        min: Math.round(
          minBy(relevantJobsCompensationData, (row) => {
            return row.measures[measure]?.min ?? 0;
          })?.measures[measure]?.min as number
        ),

        max: Math.round(
          maxBy(relevantJobsCompensationData, (row) => {
            return row.measures[measure]?.max ?? 0;
          })?.measures[measure]?.max as number
        ),

        lowest: {
          percentile: BOTTOM_PERCENTILE,
          value: Math.round(
            weightedMeanBy(relevantJobsCompensationData, (row) => {
              return [row.measures[measure]?.lowest ?? null, row.weight];
            })
          ),
        },

        low: {
          percentile: options.percentiles.low,
          value: Math.round(
            weightedMeanBy(relevantJobsCompensationData, (row) => {
              return [row.measures[measure]?.low ?? null, row.weight];
            })
          ),
        },

        middle: {
          percentile: options.percentiles.middle,
          value: Math.round(
            weightedMeanBy(relevantJobsCompensationData, (row) => {
              return [row.measures[measure]?.middle ?? null, row.weight];
            })
          ),
        },

        high: {
          percentile: options.percentiles.high,
          value: Math.round(
            weightedMeanBy(relevantJobsCompensationData, (row) => {
              return [row.measures[measure]?.high ?? null, row.weight];
            })
          ),
        },

        highest: {
          percentile: TOP_PERCENTILE,
          value: Math.round(
            weightedMeanBy(relevantJobsCompensationData, (row) => {
              return [row.measures[measure]?.highest ?? null, row.weight];
            })
          ),
        },

        target: {
          percentile: options.targetPercentile ?? options.percentiles.middle,
          value: Math.round(
            weightedMeanBy(relevantJobsCompensationData, (row) => {
              return [row.measures[measure]?.target ?? null, row.weight];
            })
          ),
        },
      };
    })
    .value() as Record<T, MarketDataCompensationData & { dataQuality: DataQuality }>;

  return {
    missingJobs,
    data: {
      employeesCount,
      distinctCompaniesCount,
      measures,
    },
  };
};

type ComputeMarketCompensationDataResult<T extends Measure> = {
  missingJobs: Job[];
  data: null | {
    employeesCount: number;
    distinctCompaniesCount: number;
    measures: Record<T, MarketDataCompensationData>;
  };
};
